<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="false"
    :style="{ width: 'clamp(320px, 92vw, 1100px)' }"
    class="!bg-[var(--p-surface-100)] dark:!bg-[var(--p-surface-900)] px-8"
  >
    <template #header>
      <div class="flex flex-col w-full justify-center ">
        <div class="flex flex-col items-center gap-5 text-center p-10">
          <div class="flex flex-row gap-3">
            <div class="w-fit shrink-0 rounded-md bg-[var(--p-primary-color)]/20 p-1">
              <IconChatTransfer width="30" height="30" class="text-[var(--p-primary-color)]" />
            </div>
            <label
              class="montserrat text-xl font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-text-color)] sm:text-2xl"
            >
              {{ t('whatsapp.transfer_chat') }}
            </label>
          </div>
        </div>
        <!-- Ambas filas usan el mismo grid -->
        <div class="flex flex-col items-center justify-center w-full gap-4 ">
          <div class="flex flex-row items-center gap-6 w-full max-w-5xl ">
            <div class="flex-1 min-w-0">
              <div class="flex w-full items-center justify-between px-2  pb-2">
                <label
                  class="text-sm font-medium text-left text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                >
                  {{ t('whatsapp.the_agent') }}
                </label>

                <label
                  class="text-sm font-medium text-right text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                >
                  {{ t('whatsapp.to_transfer_you') }}
                </label>
              </div>
              <TransferSummaryCards
                layout="pair"
                :contact-name="fromUserName"
                :contact-phone="fromCampaignName || fromCampaignId"
                :destination-name="contactName"
                :destination-subtitle="contactPhone"
                destination-label="Contacto"
              />
            </div>

            <div
              class="mt-5 flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-full bg-[var(--p-primary-color)]/20 text-[var(--p-primary-color)]"
            >
              <div class="text-3xl font-medium tabular-nums">{{ formattedCountdown }}</div>
            </div>
          </div>

          
          <div class="flex flex-row items-center gap-4 w-full max-w-md">
            <div class="flex-1 min-w-0 justify-center">
              
            </div>
            
          </div>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-5">
      <div
        v-if="transferMessage"
        class="rounded-2xl border border-[var(--p-surface-200)] bg-[var(--p-surface-0)] p-4 dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
      >
        <div class="flex items-start gap-3">
          <MessageSquareMore
            :size="20"
            class="mt-0.5 text-[var(--p-primary-color)] dark:text-[var(--p-primary-300)]"
          />
          <div class="flex flex-col gap-1">
            <span
              class="text-xs font-semibold text-[var(--p-primary-color)]/80 dark:text-[var(--p-primary-300)]"
            >
              Mensaje de la transferencia
            </span>
            <p
              class="m-0 whitespace-pre-wrap text-sm text-[var(--p-surface-700)] dark:text-[var(--p-text-color)]"
            >
              {{ transferMessage }}
            </p>
          </div>
        </div>
      </div>

      <!-- <div
        class="rounded-2xl border border-[var(--p-primary-color)]/15 bg-[var(--p-primary-color)]/6 px-4 py-3 text-sm text-[var(--p-primary-color)] dark:border-[var(--p-primary-color)]/20 dark:bg-[var(--p-primary-color)]/10 dark:text-[var(--p-primary-200)]"
      >
        Deseas aceptar esta transferencia? El chat sera asignado a ti.
      </div> -->
    </div>

    <template #footer>
      <div class="flex flex-row w-full justify-center items-center mt-3 gap-5">
        <AppButtonIcon
          :label="`Rechazar (${countdownSeconds}s)`"
          severity="secondary"
          outlined
          size="small"
          class="montserrat whitespace-nowrap w-full text-md!"
          :disabled="isProcessing"
          @click="handleReject"
        >
          <template #icon>
            <X :size="16" />
          </template>
        </AppButtonIcon>
        <AppButtonIcon
          label="Aceptar"
          size="small"
          class="montserrat whitespace-nowrap w-full transition-colors duration-200 hover:opacity-90 text-md!"
          iconPos="right"
          :disabled="isProcessing"
          @click="handleAccept"
        >
          <template #icon>
            <Check :size="16" />
          </template>
        </AppButtonIcon>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { X, Check, MessageSquareMore } from 'lucide-vue-next'
import TransferSummaryCards from './steps-transfers/TransferSummaryCards.vue'
import IconChatTransfer from '@/assets/svg/transfer_chat_icon.svg?component'
import { useI18n } from 'vue-i18n'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

interface Props {
  visible: boolean
  chatId: string
  contactName: string
  contactPhone: string
  transferMessage?: string
  fromUserId: string
  fromUserName: string
  fromCampaignId: string
  fromCampaignName: string
  toUserId: string
  toUserName: string
  toCampaignId: string
  toCampaignName: string
  countdownSeconds: number
  isProcessing: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()
const emit = defineEmits<{
  'update:visible': [value: boolean]
  accept: [chatId: string]
  reject: [chatId: string]
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

const formattedCountdown = computed(() =>
  String(Math.max(props.countdownSeconds, 0)).padStart(2, '0'),
)

const handleAccept = () => {
  emit('accept', props.chatId)
}

const handleReject = () => {
  emit('reject', props.chatId)
}
</script>
